/**
 * Global Currency Utility for STUDLYF AI.
 * Automatically converts USD ($/USD) figures to Indian Rupees (₹ / INR / Lakhs / Crores)
 * and formats all monetary numbers into Indian numbering style.
 */

const RATE = 85.0; // 1 USD = 85 INR

export function formatIndianCurrency(amount, suffix = "") {
  if (amount === null || amount === undefined || isNaN(amount)) return "";
  const num = Math.abs(amount);
  
  if (num >= 10000000) { // 1 Crore
    const cr = num / 10000000;
    const formatted = cr % 1 === 0 ? cr.toFixed(0) : cr.toFixed(2).replace(/\.?0+$/, "");
    return `₹${formatted} Crore${suffix}`;
  } else if (num >= 100000) { // 1 Lakh
    const lakh = num / 100000;
    const formatted = lakh % 1 === 0 ? lakh.toFixed(0) : lakh.toFixed(2).replace(/\.?0+$/, "");
    return `₹${formatted} Lakh${suffix}`;
  } else {
    const rounded = Math.round(num);
    const s = rounded.toString();
    if (s.length <= 3) return `₹${s}${suffix}`;
    const lastThree = s.slice(-3);
    let other = s.slice(0, -3);
    let res = "";
    while (other.length > 2) {
      res = "," + other.slice(-2) + res;
      other = other.slice(0, -2);
    }
    if (other) res = other + res;
    return `₹${res},${lastThree}${suffix}`;
  }
}

export function convertUsdToInr(text) {
  if (!text || typeof text !== "string") return text;

  function parseMultiplier(valStr, unitStr) {
    let val = parseFloat(valStr.replace(/,/g, ""));
    const unit = (unitStr || "").toLowerCase();
    if (unit === "k" || unit === "thousand") val *= 1000;
    else if (unit === "m" || unit === "mn" || unit === "million") val *= 1000000;
    else if (unit === "b" || unit === "bn" || unit === "billion") val *= 1000000000;
    else if (unit === "t" || unit === "tn" || unit === "trillion") val *= 1000000000000;
    return val;
  }

  // Pattern 1: $10,000 / $10.5K / $1.5M / $500K / $29/mo / USD 50,000
  const pattern1 = /(\$|USD\s*)(\d{1,3}(?:,\d{3})*(?:\.\d+)?|\d+(?:\.\d+)?)\s*([kKMBb]|thousand|million|billion|trillion)?(\/[a-zA-Z]+)?/gi;
  let converted = text.replace(pattern1, (match, prefix, valStr, unitStr, suffix) => {
    try {
      const usdVal = parseMultiplier(valStr, unitStr);
      const inrVal = usdVal * RATE;
      return formatIndianCurrency(inrVal, suffix || "");
    } catch {
      return match;
    }
  });

  // Pattern 2: 50,000 USD / 10K USD
  const pattern2 = /(\d{1,3}(?:,\d{3})*(?:\.\d+)?|\d+(?:\.\d+)?)\s*([kKMBb]|thousand|million|billion|trillion)?\s*(?:USD|US Dollars|Dollars)/gi;
  converted = converted.replace(pattern2, (match, valStr, unitStr) => {
    try {
      const usdVal = parseMultiplier(valStr, unitStr);
      const inrVal = usdVal * RATE;
      return formatIndianCurrency(inrVal);
    } catch {
      return match;
    }
  });

  // Clean loose USD / $ symbols
  converted = converted.replace(/\bUSD\b/g, "INR");
  converted = converted.replace(/\bUS Dollars?\b/gi, "Indian Rupees");

  return converted;
}
